// Quiet: soft grounds, expressive serif headlines, open space and a few
// gentle shapes (an outlined oval, a circle, an arch). From the Still beauty
// studio concepts. Colours: accent is a second soft ground, pop a highlight.
import { flow, textBlock, brand, footer, photo, photoBand, photoCard, pairBlock, pairStory, rule, display, label, body, widthOf, nameTag , codeTicket, finePrint } from '../kit'

const base = (env) => ({ fr: env.frame, c: env.post.content, k: env.theme.c, w: widthOf(env.frame) })
const times = (c) => (c.times || []).filter(Boolean).slice(0, 3)
const italic = (t) => (t && !/\*/.test(t) ? `*${t}*` : t)
const R = 28

// The outlined oval that floats top right
const oval = (p, fr, k) => p.ellipse(fr.right - 150, fr.top + (fr.story ? 250 : 300), 170, 236, 0.35, k.ink, 2)

export default {
  freeSlots(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    oval(p, fr, k)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 178 * s), x: fr.left - 6, w: w - 300, maxLines: 3 }), { gap: 130, slot: 'headline' }),
      textBlock(env.day, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 60, flex: 1, slot: 'date' }),
      textBlock(times(c).join('  ·  '), s => ({ ...display(env, k.ink, 84 * s), x: fr.left - 4, w: w + 4, maxLines: 2 }), { gap: 18, slot: 'times' }),
      c.more ? textBlock('More times available', () => ({ ...label(env, k.mutedOnPaper), x: fr.left, w }), { gap: 20, slot: 'more' }) : null,
      footer(env, fr, k.ink),
    ])
  },

  oneSlot(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink, { align: 'center' }),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 124 * s), x: fr.left, w, align: 'center', maxLines: 3 }), { gap: 90, slot: 'headline' }),
      {
        gap: 50, flex: 1,
        draw: (pp, y, s) => {
          const r = Math.round(250 * s)
          const cx = fr.W / 2
          pp.circle(cx, y + r, r, k.accent)
          const d = pp.text(env.day, { ...label(env, k.ink), x: cx - r, y: y + r - 118 * s, w: r * 2, align: 'center', slot: 'date' })
          pp.text(c.time || '', { ...display(env, k.ink, 150 * s), x: cx - r + 20, y: d.bottom + 10 * s, w: r * 2 - 40, align: 'center', maxLines: 1, slot: 'time' })
          return y + r * 2
        },
      },
      textBlock(italic(c.service), s => ({ ...display(env, k.ink, 64 * s), x: fr.left, w, align: 'center', maxLines: 2 }), { gap: 30, slot: 'service' }),
      textBlock(c.line, () => ({ ...body(env, k.mutedOnPaper, 38), x: fr.left, w, align: 'center', maxLines: 2 }), { gap: 16, slot: 'line' }),
      footer(env, fr, k.ink, k.ink, { align: 'center' }),
    ])
  },

  freshWork(p, env) {
    const { fr, c, k } = base(env)
    const words = (f) => [
      f.story ? brand(env, f, k.ink) : null,
      textBlock(c.headline, s => ({ ...display(env, k.ink, 126 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 3 }), { gap: f.story ? 24 : 0, slot: 'headline' }),
      textBlock(c.detail, () => ({ ...label(env, k.ink), x: f.left, w: widthOf(f) }), { gap: 18, slot: 'detail' }),
      footer(env, f, k.ink),
    ]
    if (fr.story) {
      photoCard(p, env, { key: 'main', card: k.paper, radius: R, blocks: words })
      return
    }
    photoBand(p, env, { key: 'main', photoH: 865, band: k.paper, pad: 46, blocks: words })
    nameTag(p, env, 44, 44, k.paper, k.ink)
  },

  beforeAfter(p, env) {
    const { fr, c, k } = base(env)
    const words = (f) => [
      textBlock(c.headline, s => ({ ...display(env, k.ink, 112 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 2 }), { slot: 'headline' }),
      textBlock(c.detail, () => ({ ...label(env, k.ink), x: f.left, w: widthOf(f) }), { gap: 16, slot: 'detail' }),
      footer(env, f, k.ink),
    ]
    if (fr.story) {
      pairStory(p, env, { bg: k.paper, tagBg: k.paper, tagFg: k.ink, blocks: words })
      return
    }
    p.rect(0, 0, fr.W, fr.H, k.paper)
    const [head, detail, foot] = words(fr)
    flow(p, fr, [brand(env, fr, k.ink), pairBlock(env, fr, { h: 600, gap: 20, radius: R, labelsBelow: true, labelColour: k.ink }), head && { ...head, gap: 40 }, detail, foot])
  },

  review(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    const stars = Math.max(1, Math.min(5, Number(c.rating) || 5))
    flow(p, fr, [
      brand(env, fr, k.ink),
      { gap: 120, draw: (pp, y) => { pp.stars(fr.left, y, 44, stars, k.pop, 12); return y + 44 } },
      textBlock(c.quote ? `“${c.quote}”` : '', s => ({ ...display(env, k.ink, 108 * s), italic: !!env.theme.family.italic, font: env.theme.family.italic || env.theme.family.display, x: fr.left, w, maxLines: 8, maxH: 680 * s, lh: 1.04 }), { gap: 40, slot: 'quote' }),
      textBlock(c.name, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 50, flex: 1, slot: 'name' }),
      textBlock(c.service, () => ({ ...body(env, k.mutedOnPaper, 34), x: fr.left, w, maxLines: 1 }), { gap: 8, slot: 'service' }),
      footer(env, fr, k.ink),
    ])
  },

  team(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink, { align: 'center' }),
      {
        gap: 50,
        draw: (pp, y, s) => {
          const aw = Math.round(540 * s)
          const ah = Math.round(640 * s)
          photo(pp, env, 'main', { x: (fr.W - aw) / 2, y, w: aw, h: ah, radius: { tl: aw / 2, tr: aw / 2, br: R, bl: R } })
          return y + ah
        },
      },
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w, align: 'center' }), { gap: 40, slot: 'eyebrow' }),
      textBlock(c.name, s => ({ ...display(env, k.ink, 120 * s), x: fr.left, w, align: 'center', maxLines: 1 }), { gap: 10, slot: 'name' }),
      textBlock(c.role, () => ({ ...label(env, k.ink), x: fr.left, w, align: 'center' }), { gap: 12, slot: 'role' }),
      textBlock(c.line, () => ({ ...body(env, k.mutedOnPaper, 38), x: fr.left + 40, w: w - 80, align: 'center', maxLines: 2 }), { gap: 14, slot: 'line' }),
      footer(env, fr, k.ink, k.ink, { align: 'center' }),
    ])
  },

  priceList(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    const page = (env.slide && env.slide.page) || 0
    const items = (c.items || []).filter(it => it && it.name).slice(page * 4, page * 4 + 4)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.title, s => ({ ...display(env, k.ink, 150 * s), x: fr.left - 6, w: w + 6, maxLines: 2 }), { gap: 100, slot: 'title' }),
      {
        gap: 60, flex: 1,
        draw: (pp, y, s) => {
          let yy = y
          items.forEach((it, i) => {
            if (i) pp.line(fr.left, yy + 1, fr.right, yy + 1, k.mutedOnPaper, 1.5)
            const n = pp.text(it.name, { ...display(env, k.ink, Math.max(46, 58 * s)), x: fr.left, y: yy + 26, w: w - 240, maxLines: 1, slot: 'items' })
            const d = it.detail ? pp.text(it.detail, { ...label(env, k.mutedOnPaper), x: fr.left, y: n.bottom + 8, w: w - 240 }) : n
            const pr = pp.text(it.price || '', { ...body(env, k.ink, 42), x: fr.right - 220, y: yy + 30, w: 220, align: 'right', maxLines: 1, slot: 'items' })
            yy = Math.max(d.bottom, pr.bottom) + 26
          })
          return yy
        },
      },
      textBlock(c.note, () => ({ ...body(env, k.mutedOnPaper, 34), x: fr.left, w, maxLines: 2 }), { gap: 20, slot: 'note' }),
      footer(env, fr, k.ink),
    ])
  },

  tips(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      {
        gap: 100,
        draw: (pp, y) => {
          pp.circle(fr.right - 70, y + 50, 70, k.pop)
          pp.text(c.number || '', { ...display(env, k.paper, 72), x: fr.right - 140, y: y + 50 - 72 * 0.5, w: 140, align: 'center', maxLines: 1, minSize: 40, slot: 'number' })
          const l = pp.text(c.label || '', { ...label(env, k.ink), x: fr.left, y: y + 32, w: w - 180, slot: 'label' })
          return Math.max(l.bottom, y + 120)
        },
      },
      textBlock(c.headline, s => ({ ...display(env, k.ink, 126 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 50, slot: 'headline' }),
      rule(fr, k.ink, { gap: 50, width: 1.5 }),
      textBlock(c.body, () => ({ ...body(env, k.ink, 44), x: fr.left, w: w - 30, maxLines: 5 }), { gap: 40, slot: 'body' }),
      footer(env, fr, k.ink),
    ])
  },

  question(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      {
        gap: 110,
        draw: (pp, y, s) => {
          pp.circle(fr.right - 56, y + 56, 56, k.pop)
          pp.text('?', { ...display(env, k.paper, 64), x: fr.right - 112, y: y + 56 - 64 * 0.52, w: 112, align: 'center', maxLines: 1 })
          return pp.text(c.headline || '', { ...display(env, k.ink, 140 * s), x: fr.left - 6, y, w: w - 150, maxLines: 4, slot: 'headline' }).bottom
        },
      },
      rule(fr, k.ink, { gap: 64, width: 1.5 }),
      textBlock(c.body, () => ({ ...body(env, k.ink, 46), x: fr.left, w: w - 30, maxLines: 5 }), { gap: 44, slot: 'body' }),
      footer(env, fr, k.ink),
    ])
  },

  // An announcement: a season, a date, some news, softly
  announce(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    oval(p, fr, k)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w: w - 300 }), { gap: 130, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 150 * s), x: fr.left - 6, w: w - 120, maxLines: 4 }), { gap: 30, flex: 1, slot: 'headline' }),
      rule(fr, k.ink, { gap: 60, width: 1.5 }),
      textBlock(c.body, () => ({ ...body(env, k.ink, 44), x: fr.left, w: w - 30, maxLines: 4 }), { gap: 40, slot: 'body' }),
      footer(env, fr, k.ink),
    ])
  },

  offer(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.accent)
    oval(p, fr, k)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 110, slot: 'eyebrow' }),
      textBlock(c.value, s => ({ ...display(env, k.ink, 210 * s), x: fr.left - 6, w: w - 60, maxLines: 2 }), { gap: 24, slot: 'value' }),
      textBlock(italic(c.service), s => ({ ...display(env, k.ink, 66 * s), x: fr.left, w, maxLines: 2 }), { gap: 16, slot: 'service' }),
      textBlock(c.price, () => ({ ...body(env, k.ink, 40), x: fr.left, w, maxLines: 2 }), { gap: 56, flex: 1, slot: 'price' }),
      textBlock(c.dates, () => ({ ...body(env, k.mutedOnPaper, 36), x: fr.left, w, maxLines: 1 }), { gap: 8, slot: 'dates' }),
      codeTicket(env, fr, k.ink),
      finePrint(env, fr, k.ink),
      footer(env, fr, k.ink),
    ])
  },

  giftVouchers(p, env) {
    const { fr, c, k, w } = base(env)
    p.rect(0, 0, fr.W, fr.H, k.paper)
    flow(p, fr, [
      brand(env, fr, k.ink),
      textBlock(c.eyebrow, () => ({ ...label(env, k.ink), x: fr.left, w }), { gap: 100, slot: 'eyebrow' }),
      textBlock(c.headline, s => ({ ...display(env, k.ink, 124 * s), x: fr.left - 4, w: w + 4, maxLines: 3 }), { gap: 30, slot: 'headline' }),
      {
        gap: 60, flex: 1,
        draw: (pp, y, s) => {
          const cw = Math.min(w, 780)
          const ch = Math.round(440 * s)
          pp.rect(fr.left, y, cw, ch, k.accent, R)
          pp.text(env.theme.name, { ...display(env, k.ink, 54 * s), x: fr.left + 48, y: y + 44, w: cw - 360, maxLines: 1 })
          pp.text('Gift voucher', { ...label(env, k.ink), x: fr.left + cw - 330, y: y + 52, w: 282, align: 'right' })
          pp.text(c.from || '', { ...display(env, k.ink, 112 * s), x: fr.left + 48, y: y + ch - 48 - 112 * s * 0.97, w: cw - 96, maxLines: 1, slot: 'from' })
          return y + ch
        },
      },
      footer(env, fr, k.ink),
    ])
  },
}
